import { X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import CandleChart from "src/common/charts/candleChart";
import ChartSkeleton from "src/components/skeleton/ChartSkeleton";
import { useSocket } from "src/components/SocketContext";
import {
  candleDetailsModalWrapperClass,
  DataPoint,
  LtpMessage,
  renderCandleDetailsReact,
} from "src/helpers";

interface InsiderChartProps {
  isOpen: boolean;
  onClose: () => void;
  darkMode: boolean | undefined;
  stock: DataPoint[];
  selectedSymbol: string | undefined;
  aquisitionFromDate: string;
  aquisitionToDate: string;
  transactionType: string;
  candleLoading: boolean;
}

const InsiderChartModal = (props: InsiderChartProps) => {
  const {
    isOpen,
    onClose,
    darkMode,
    stock,
    selectedSymbol,
    aquisitionFromDate,
    aquisitionToDate,
    transactionType,
    candleLoading,
  } = props;
  const [hoveredCandle, setHoveredCandle] = useState<DataPoint | null>(null);
  const [liveCandleUpdate, setLiveCandleUpdate] =
    useState<Partial<DataPoint> | null>(null);
  const { emit, on, off, isConnected } = useSocket();

  useEffect(() => {
    if (!selectedSymbol || !isConnected || !isOpen) return;

    const handleStockUpdate = (messages: LtpMessage[]) => {
      const updated = messages.find(
        (m: LtpMessage) => m.symbol_name === selectedSymbol,
      );
      if (updated) {
        setLiveCandleUpdate({
          open: Number(updated.open),
          close: Number(updated.ltp),
          high: Number(updated.high),
          low: Number(updated.low),
          volume: Number(updated.volume),
          date: updated.ltp_date ? new Date(updated.ltp_date) : undefined,
        });
      }
    };

    on("stock_update", handleStockUpdate);
    emit("subscribe_stock", { symbol: [selectedSymbol] });

    return () => {
      off("stock_update", handleStockUpdate);
      emit("unsubscribe_stock", { symbol: [selectedSymbol] });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSymbol, isConnected, isOpen]);

  // Reset live update when symbol changes or modal closes
  useEffect(() => {
    if (!isOpen) {
      setLiveCandleUpdate(null);
    }
  }, [selectedSymbol, isOpen]);

  const activeCandle = useMemo(() => {
    if (hoveredCandle) return hoveredCandle;
    const lastDataCandle = stock?.length > 0 ? stock[stock.length - 1] : null;
    if (liveCandleUpdate && lastDataCandle) {
      return {
        ...lastDataCandle,
        ...liveCandleUpdate,
      };
    }
    return lastDataCandle;
  }, [hoveredCandle, stock, liveCandleUpdate]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-sm p-4 h-full"
      onClick={onClose}
    >
      <div
        className="w-full max-w-7xl rounded-2xl overflow-hidden shadow-2xl z-10 bg-white dark:bg-gray-900/80 backdrop-blur-lg p-1 relative lg:w-full m-4"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-2 right-3 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white cursor-pointer"
          onClick={onClose}
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex flex-col md:flex-row md:items-center gap-1.5 md:gap-4 mb-2 ml-3">
          <h2 className="text-lg dark:text-white font-semibold whitespace-nowrap">
            {selectedSymbol}: 1 Year Chart (TF: 1 Day)
          </h2>
          {!candleLoading && stock?.length > 0 && (
            <div className={candleDetailsModalWrapperClass}>
              {renderCandleDetailsReact(activeCandle as DataPoint)}
            </div>
          )}
        </div>

        <div className="text-gray-700 mb-0 w-full h-100">
          {candleLoading ? (
            <ChartSkeleton />
          ) : stock?.length === 0 ? (
            <div className="text-gray-500 text-center py-25">No data found</div>
          ) : (
            <CandleChart
              symbolName={selectedSymbol}
              candleData={stock}
              darkMode={!!darkMode}
              fromDate={aquisitionFromDate}
              toDate={aquisitionToDate}
              transactionType={transactionType}
              isExpand={true}
              onHoverChange={setHoveredCandle}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default InsiderChartModal;
